export type CookieCategory = 'essential' | 'analytics' | 'functional' | 'marketing';

export type CookiePreferences = {
  essential: true;
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
};

export type ConsentSource = 'accept_all' | 'reject_non_essential' | 'customize';

export type CookieConsentRecord = {
  version: string;
  updatedAt: string;
  source: ConsentSource;
  preferences: CookiePreferences;
};

export const COOKIE_POLICY_PATH = '/cookie-policy';
export const COOKIE_CONSENT_VERSION = '2026-04-12';
export const COOKIE_CONSENT_STORAGE_KEY = 'figtures-cookie-consent';
export const COOKIE_CONSENT_EVENT = 'figtures:cookie-consent-updated';
export const COOKIE_PREFERENCES_OPEN_EVENT = 'figtures:cookie-preferences-open';

export type CookieCategoryDefinition = {
  key: CookieCategory;
  title: { tr: string; en: string };
  description: { tr: string; en: string };
  required: boolean;
};

export const COOKIE_CATEGORY_DEFINITIONS: CookieCategoryDefinition[] = [
  {
    key: 'essential',
    required: true,
    title: {
      tr: 'Zorunlu Çerezler',
      en: 'Essential Cookies',
    },
    description: {
      tr: 'Site güvenliği, oturum bütünlüğü ve temel navigasyon için gereklidir. Kapatılamaz.',
      en: 'Required for security, session integrity, and core navigation. Cannot be disabled.',
    },
  },
  {
    key: 'analytics',
    required: false,
    title: {
      tr: 'Analitik Çerezler',
      en: 'Analytics Cookies',
    },
    description: {
      tr: 'Site performansını ve kullanım desenlerini anlamamıza yardımcı olur.',
      en: 'Help us understand site performance and usage patterns.',
    },
  },
  {
    key: 'functional',
    required: false,
    title: {
      tr: 'Fonksiyonel Çerezler',
      en: 'Functional Cookies',
    },
    description: {
      tr: 'Dil, tercih ve kişiselleştirme gibi deneyim özelliklerini destekler.',
      en: 'Support experience features such as language, preferences, and personalization.',
    },
  },
  {
    key: 'marketing',
    required: false,
    title: {
      tr: 'Pazarlama Çerezleri',
      en: 'Marketing Cookies',
    },
    description: {
      tr: 'Pazarlama kampanyalarının ölçümü ve reklam etkinliği analizleri için kullanılabilir.',
      en: 'May be used for campaign measurement and advertising effectiveness analysis.',
    },
  },
];

export function getDefaultCookiePreferences(): CookiePreferences {
  return {
    essential: true,
    analytics: false,
    functional: false,
    marketing: false,
  };
}

export function withAllNonEssentialEnabled(): CookiePreferences {
  return {
    essential: true,
    analytics: true,
    functional: true,
    marketing: true,
  };
}

function canUseBrowserStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function readCookieConsent(): CookieConsentRecord | null {
  if (!canUseBrowserStorage()) return null;

  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<CookieConsentRecord>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== COOKIE_CONSENT_VERSION) return null;

    const pref = parsed.preferences;
    if (!pref || typeof pref !== 'object') return null;

    return {
      version: COOKIE_CONSENT_VERSION,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
      source:
        parsed.source === 'accept_all' ||
        parsed.source === 'reject_non_essential' ||
        parsed.source === 'customize'
          ? parsed.source
          : 'customize',
      preferences: {
        essential: true,
        analytics: Boolean(pref.analytics),
        functional: Boolean(pref.functional),
        marketing: Boolean(pref.marketing),
      },
    };
  } catch {
    return null;
  }
}

export function persistCookieConsent(input: {
  source: ConsentSource;
  preferences: CookiePreferences;
}) {
  if (!canUseBrowserStorage()) return;

  const record: CookieConsentRecord = {
    version: COOKIE_CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    source: input.source,
    preferences: {
      essential: true,
      analytics: Boolean(input.preferences.analytics),
      functional: Boolean(input.preferences.functional),
      marketing: Boolean(input.preferences.marketing),
    },
  };

  window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: record }));
}

export function shouldShowCookieConsentBanner() {
  return readCookieConsent() == null;
}

export function hasCookieConsentFor(category: CookieCategory) {
  const current = readCookieConsent();
  if (!current) return false;
  if (category === 'essential') return true;
  return Boolean(current.preferences[category]);
}

/** Lets any surface (footer, cookie policy page) reopen the preference dialog. */
export function openCookiePreferences() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(COOKIE_PREFERENCES_OPEN_EVENT));
}

export function onCookiePreferencesOpen(handler: () => void) {
  if (typeof window === 'undefined') return () => {};

  window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handler);
  return () => window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handler);
}

/**
 * Future-facing helper for analytics/marketing script gating.
 * Consumers can subscribe once and react to preference changes.
 */
export function onCookieConsentChange(handler: (record: CookieConsentRecord) => void) {
  if (typeof window === 'undefined') return () => {};

  const listener = (event: Event) => {
    const custom = event as CustomEvent<CookieConsentRecord>;
    if (custom.detail) handler(custom.detail);
  };

  window.addEventListener(COOKIE_CONSENT_EVENT, listener);
  return () => window.removeEventListener(COOKIE_CONSENT_EVENT, listener);
}
